/**
 * Cookie helpers for server-side auth flows.
 *
 * These deliberately operate on opaque values. Access and refresh tokens must
 * never be decoded or logged by the frontend server.
 */

export function extractSetCookieValue(
  setCookies: string[],
  cookieName: string
): string | undefined {
  const prefix = `${cookieName}=`;
  const cookie = setCookies.find((value) => value.trimStart().startsWith(prefix));
  if (!cookie) return undefined;

  return cookie.trimStart().slice(prefix.length).split(';', 1)[0];
}

/**
 * Reads one exact cookie name from a request Cookie header. Values remain
 * opaque: callers must not decode or log credentials extracted here.
 */
export function extractCookieHeaderValue(
  cookieHeader: string | null | undefined,
  cookieName: string
): string | undefined {
  if (!cookieHeader) return undefined;

  const prefix = `${cookieName}=`;
  const segment = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return segment?.slice(prefix.length);
}

/**
 * Replaces or appends exactly one cookie in a request Cookie header.
 *
 * Rebuilding the list avoids regex replacements that can accidentally consume
 * the semicolon before a middle cookie and concatenate two credentials.
 */
export function upsertCookieHeader(
  cookieHeader: string,
  cookieName: string,
  value: string
): string {
  const prefix = `${cookieName}=`;
  const cookies = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);

  let found = false;
  const updated = cookies.map((cookie) => {
    if (!cookie.startsWith(prefix)) return cookie;
    found = true;
    return `${cookieName}=${value}`;
  });

  if (!found) updated.push(`${cookieName}=${value}`);
  return updated.join('; ');
}
